import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { toast, getToastActionCallback, clearToastActionCallback } from './toastActions';
import { ToastItem, ToastSeverity, ToastState, ToastAnchorOrigin } from './toastReducer';
import tokens from '@/theme/tokens';
import radii from '@/theme/radii';

const SEVERITY_ICONS: Record<ToastSeverity, React.ElementType> = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon,
};

/**
 * Auto-dismissing toasts announce politely; the two that persist until the user
 * acts announce assertively. Before this, nothing was announced at all.
 */
const SEVERITY_ROLE: Record<ToastSeverity, 'status' | 'alert'> = {
  success: 'status',
  info: 'status',
  error: 'alert',
  warning: 'alert',
};

/** 180ms ease-out rise, per the design's "Entering" state. */
const ENTER_MS = 180;
/** 140ms ease-in fade and slide, per the design's "Leaving" state. */
const EXIT_MS = 140;
/** Both collapse to a plain opacity fade under `prefers-reduced-motion`. */
const REDUCED_MS = 100;

const containerSx = (anchor: ToastAnchorOrigin) => {
  const sx: any = {
    position: 'fixed',
    zIndex: (t: any) => t.zIndex.snackbar,
    display: 'flex',
    flexDirection: anchor.vertical === 'top' ? 'column' : 'column-reverse',
    // 10px between stacked toasts, 24px from the viewport edges — 16px on a
    // narrow viewport, where the toast runs full width between the gutters.
    gap: tokens.space.px10,
    p: { xs: tokens.space.px16, sm: tokens.space.px24 },
    pointerEvents: 'none',
    left: 0,
    right: 0,
    alignItems:
      anchor.horizontal === 'left'
        ? 'flex-start'
        : anchor.horizontal === 'right'
        ? 'flex-end'
        : 'center',
  };
  sx[anchor.vertical] = 0;
  return sx;
};

const groupKey = (a: ToastAnchorOrigin) => `${a.vertical}-${a.horizontal}`;

/**
 * The single action a toast may carry: underlined text in the status
 * foreground, never a filled button. Pressing swaps the underline for a tint
 * chip, which is the only state the design gives it.
 */
const ActionLabel: React.FC<{
  label: string;
  fg: string;
  chip: string;
  onClick: () => void;
}> = ({ label, fg, chip, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      // `label` is the theme's 13px/700 field-label and status-line role — the
      // design's own size, at the theme's bold rather than an 800 it has no
      // weight for.
      typography: 'label',
      color: fg,
      bgcolor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      p: tokens.space.toastActionPadding,
      borderRadius: `${radii.sm}px`,
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      '&:active': { textDecoration: 'none', bgcolor: chip },
      '&:focus-visible': { outline: `2px solid ${fg}`, outlineOffset: 2 },
    }}
  >
    {label}
  </Box>
);

const ToastRow: React.FC<{ item: ToastItem }> = ({ item }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  /**
   * Placement is decided in JS rather than with two responsive `display` rules
   * so the action exists once in the DOM — a duplicated node would give the
   * toast two buttons with the same accessible name.
   */
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * `palette.tint` keys its four severity pairs by severity name, so this is a
   * direct index rather than a lookup table — a success toast and a success chip
   * read the same pair by construction.
   *
   * `info` is the one place this app departs from the tint-pair toast design
   * doc: the doc draws info and progress in teal (`primary.dark`), which here is
   * the banner and selection colour, not the status-info colour. Info stays
   * blue.
   */
  const tint = theme.palette.tint[item.severity];
  const closeChip = tokens.designSystem.toast.closeChip[item.severity];
  const SeverityIcon = SEVERITY_ICONS[item.severity];
  const exitMs = reducedMotion ? REDUCED_MS : EXIT_MS;

  const [exiting, setExiting] = useState(false);

  /**
   * The dismiss timer lives here rather than in the reducer so pause-on-hover
   * needs no store change and no call-site change. `remaining` is what is left
   * of `hideDuration`; hovering banks the unspent part and leaving resumes from
   * it, never from the top.
   */
  const remaining = useRef<number | null>(item.hideDuration);
  const startedAt = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closing = useRef(false);

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    startedAt.current = null;
  };

  /**
   * Dismissal is two steps: mark the row `exiting` so it can animate, then drop
   * it from the store once the animation has run. The row has to stay mounted
   * for those `exitMs` — a store-first removal unmounts it and there is nothing
   * left to animate, which is why the toast had no leave transition before.
   */
  const beginClose = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    clearTimer();
    setExiting(true);
    exitTimer.current = setTimeout(() => {
      clearToastActionCallback(item.id);
      dispatch(toast.hide(item.id));
    }, exitMs);
  }, [dispatch, exitMs, item.id]);

  const startTimer = useCallback(() => {
    if (remaining.current == null || closing.current || timer.current) return;
    startedAt.current = Date.now();
    timer.current = setTimeout(beginClose, remaining.current);
  }, [beginClose]);

  const pauseTimer = () => {
    if (timer.current == null || startedAt.current == null) return;
    const spent = Date.now() - startedAt.current;
    clearTimer();
    remaining.current = Math.max(0, (remaining.current ?? 0) - spent);
  };

  useEffect(() => {
    startTimer();
    return () => {
      clearTimer();
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
    // Mount only: the row is keyed by `item.id`, so a different toast is a
    // different row rather than a re-run here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAction = () => {
    const cb = getToastActionCallback(item.id);
    if (cb) cb();
    beginClose();
  };

  /**
   * A title-only toast is one row, so glyph, title, action and close centre on
   * it. Only the body variant is top-aligned, where the glyph has to sit against
   * the title's first line rather than float beside two lines of text. The design
   * draws its status set exactly this way — `center` for every single-line toast,
   * `flex-start` for the one carrying body copy.
   *
   * On a narrow viewport the action wraps below the title, which makes even a
   * body-less toast two rows, so that case top-aligns too.
   */
  const singleRow = !item.body && !(narrow && item.actionLabel);

  return (
    <Paper
      role={SEVERITY_ROLE[item.severity]}
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
      onFocus={pauseTimer}
      onBlur={startTimer}
      sx={{
        display: 'flex',
        alignItems: singleRow ? 'center' : 'flex-start',
        gap: tokens.space.px12,
        width: { xs: '100%', sm: 'auto' },
        minWidth: { sm: 380 },
        maxWidth: { xs: '100%', sm: 440 },
        p: tokens.space.toastPadding,
        borderRadius: `${radii.xl}px`,
        bgcolor: tint.bg,
        border: `1px solid ${tint.bd}`,
        boxShadow: tokens.designSystem.toast.shadow,
        pointerEvents: 'auto',
        // The hover lift doubles as the only tell that the timer is paused —
        // the design's timer ring, which shows it properly, is deferred.
        '&:hover': { boxShadow: tokens.designSystem.toast.shadowHover },
        animation: `toastIn ${reducedMotion ? REDUCED_MS : ENTER_MS}ms ease-out both`,
        '@keyframes toastIn': reducedMotion
          ? { from: { opacity: 0 }, to: { opacity: 1 } }
          : {
              from: { opacity: 0, transform: 'translateY(8px) scale(0.985)' },
              to: { opacity: 1, transform: 'none' },
            },
        transition: `opacity ${exitMs}ms ease-in, transform ${exitMs}ms ease-in`,
        ...(exiting && {
          opacity: 0,
          // Toward the edge the stack sits against — upward at a top anchor.
          transform: reducedMotion
            ? 'none'
            : `translateY(${item.anchorOrigin.vertical === 'top' ? '-10px' : '10px'})`,
        }),
      }}
    >
      <SeverityIcon
        aria-hidden
        sx={{
          flex: 'none',
          color: tint.fg,
          fontSize: tokens.fontSize.toastSeverityIcon,
          // Nudged onto the title's cap height only when the row is top-aligned;
          // a centred row needs no correction.
          mt: singleRow ? tokens.space.px0 : tokens.space.px1,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/*
          `h6` and `body2` rather than the design's own 14.5px/13px: those are
          the theme's nearest existing variants (Lato 700 / 15px and Roboto 400
          / 13.5px), and a toast is not worth two more one-off type sizes. The
          title is a small heading and the body is helper text, which is what
          each variant is for.
        */}
        <Typography variant="h6" sx={{ color: tint.fg }}>
          {item.message}
        </Typography>
        {item.body && (
          <Typography
            variant="body2"
            sx={{ mt: tokens.space.px3, color: tokens.color.toastBodyInk }}
          >
            {item.body}
          </Typography>
        )}
        {/*
          Below 600px the action drops to its own right-aligned row rather than
          squeezing the title, per the design's narrow-viewport rule.
        */}
        {item.actionLabel && narrow && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: tokens.space.px8 }}>
            <ActionLabel
              label={item.actionLabel}
              fg={tint.fg}
              chip={closeChip}
              onClick={handleAction}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: tokens.space.px6, flex: 'none' }}
      >
        {item.actionLabel && !narrow && (
          <ActionLabel
            label={item.actionLabel}
            fg={tint.fg}
            chip={closeChip}
            onClick={handleAction}
          />
        )}
        <IconButton
          aria-label="close"
          onClick={beginClose}
          sx={{
            // 26px glyph box, 44px hit target. The target is a pseudo-element
            // rather than padding: as padding it would be 44px of layout, which
            // made a one-line toast 72px tall with the glyph and title floating
            // in the middle of it.
            width: 26,
            height: 26,
            p: 0,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: tokens.space.toastCloseHitInset,
            },
            borderRadius: `${radii.sm}px`,
            color: tint.fg,
            opacity: 0.55,
            '&:hover': { opacity: 1, bgcolor: closeChip },
            '&.Mui-focusVisible': {
              opacity: 1,
              outline: `2px solid ${theme.palette.primary.dark}`,
              outlineOffset: 2,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: tokens.fontSize.toastCloseIcon }} />
        </IconButton>
      </Box>
    </Paper>
  );
};

const Toast: React.FC = () => {
  const toasts = useSelector((state: any) => (state.toast as ToastState).toasts);

  const groups = new Map<string, { anchor: ToastAnchorOrigin; items: ToastItem[] }>();
  toasts.forEach((t) => {
    const key = groupKey(t.anchorOrigin);
    if (!groups.has(key)) groups.set(key, { anchor: t.anchorOrigin, items: [] });
    groups.get(key)!.items.push(t);
  });

  return (
    <>
      {Array.from(groups.entries()).map(([key, { anchor, items }]) => (
        <Box key={key} sx={containerSx(anchor)}>
          {items.map((item) => (
            <ToastRow key={item.id} item={item} />
          ))}
        </Box>
      ))}
    </>
  );
};

export default Toast;
